import { useEffect, useState } from "react";
import { PDFDocument } from "pdf-lib";
import toast from "react-hot-toast";
import { Capacitor } from "@capacitor/core";
import { Filesystem, Directory } from "@capacitor/filesystem";
import { formatAmount, formatDateDDMMYYYY } from "../../../Utils";
import { TOAST_MESSAGES } from "../constants";

const arrayBufferToBase64 = (buffer) => {
  let binary = "";
  const bytes = new Uint8Array(buffer);
  for (let i = 0; i < bytes.byteLength; i++) {
    binary += String.fromCharCode(bytes[i]);
  }
  return window.btoa(binary);
};

export const useSalarySlipPdf = () => {
  const [pdfTemplate, setPdfTemplate] = useState(null);

  /* ---------------- Load PDF Template ---------------- */
  useEffect(() => {
    const loadTemplate = async () => {
      try {
        const bytes = await fetch("/pdf/salary_slip_f5.pdf").then((res) =>
          res.arrayBuffer()
        );
        setPdfTemplate(await PDFDocument.load(bytes));
      } catch {
        toast.error(TOAST_MESSAGES.PDF_TEMPLATE_LOAD_FAILED);
      }
    };

    loadTemplate();
  }, []);

  /* ---------------- Field Mapping ---------------- */
  const getSalarySlipFields = (data) => [
    {
      name: "pay_month",
      value: data?.start_date
        ? new Date(data.start_date).toLocaleString("en-US", {
            month: "long",
            year: "numeric",
          })
        : "",
    },
    {
      name: "employee_name",
      value: `${data?.userDetails?.first_name ?? ""} ${
        data?.userDetails?.last_name ?? ""
      }`,
    },
    { name: "designation", value: data?.userDetails?.designation },
    {
      name: "date_of_joining",
      value: formatDateDDMMYYYY(data?.userDetails?.date_of_joining),
    },

    { name: "gross_wage", value: formatAmount(data?.gross) },
    { name: "total_working_days", value: data?.total_days },
    { name: "lop_days", value: data?.lop_count },
    { name: "ncns_count", value: data?.ncns_count },
    { name: "paid_days", value: data?.paid_days },
    { name: "leaves_taken", value: data?.paid_leave_days },

    { name: "uan", value: data?.userDetails?.uan_number },
    { name: "acc_no", value: data?.userDetails?.salary_account_number },
    { name: "bank_name", value: data?.userDetails?.bank_name },

    { name: "basic_wage", value: formatAmount(data?.basic_amount) },
    { name: "hra", value: formatAmount(data?.hra) },
    {
      name: "conveyance_allowances",
      value: formatAmount(data?.conveyance_allowance),
    },
    { name: "da", value: formatAmount(data?.da) },
    { name: "medical_allowance", value: formatAmount(data?.medical_allowance) },
    { name: "attendance_allowance", value: formatAmount(data?.attendance_allowance) },
    { name: "washing_allowance", value: formatAmount(data?.washing_allowance) },
    { name: "performance_allowance", value: formatAmount(data?.performance_allowance) },
    { name: "referral_bonus", value: formatAmount(data?.referral_bonus) },
    { name: "other_adjustments", value: formatAmount(data?.other_adjustments) },

    { name: "employee_pf_share", value: formatAmount(data?.employee_pf) },
    { name: "employers_pf_share", value: formatAmount(data?.employer_pf) },
    { name: "employee_esic", value: formatAmount(data?.employee_esic) },
    { name: "employers_esic", value: formatAmount(data?.employer_esic) },
    { name: "professional_tax", value: formatAmount(data?.pt) },
    { name: "tds", value: formatAmount(data?.tds) },
    { name: "leave_deductions", value: formatAmount(data?.leave_deductions) },

    { name: "total_deductions", value: formatAmount(data?.total_deductions) },
    { name: "total_earnings", value: formatAmount(data?.gross) },
    { name: "net_salary", value: formatAmount(data?.net) },
  ];

  /* ---------------- Download Logic ---------------- */
  const downloadSalarySlipPdf = async (data) => {
    try {
      if (!pdfTemplate) {
        toast.error(TOAST_MESSAGES.PDF_TEMPLATE_LOADING);
        return;
      }

      const pdf = await PDFDocument.load(await pdfTemplate.save());
      const form = pdf.getForm();

      getSalarySlipFields(data).forEach(({ name, value }) => {
        const field = form.getTextField(name);
        if (field) field.setText(value != null ? String(value) : "");
      });

      form.flatten();
      const pdfBytes = await pdf.save();

      const fileName = `SalarySlip_${data?.userDetails?.first_name}_${data?.userDetails?.last_name}.pdf`;

      if (Capacitor.getPlatform() === "web") {
        const blob = new Blob([pdfBytes], { type: "application/pdf" });
        const url = URL.createObjectURL(blob);
        Object.assign(document.createElement("a"), {
          href: url,
          download: fileName,
        }).click();
        URL.revokeObjectURL(url);
      } else {
        await Filesystem.writeFile({
          path: fileName,
          data: arrayBufferToBase64(pdfBytes),
          directory: Directory.Documents,
          recursive: true,
        });
      }

      toast.success(TOAST_MESSAGES.PDF_DOWNLOAD_SUCCESS);
    } catch (err) {
      toast.error(TOAST_MESSAGES.PDF_DOWNLOAD_FAILED);
      console.error(err);
    }
  };

  return { downloadSalarySlipPdf };
};
